import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../axiosinstance";
import "./InvoiceReport.css";
import Pagination from "../Components/Pagination";
import { FiFileText } from "react-icons/fi";
import { TbCurrencyRupee } from "react-icons/tb";
import SearchableDropdown from "../Components/SearchableDropdown";


const INR_CURRENCY = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const INR_NUMBER = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatINR(value, { symbol = true } = {}) {
  const num = Number(value ?? 0);
  if (!isFinite(num)) return symbol ? "Rs0.00" : "0.00";
  return (symbol ? INR_CURRENCY : INR_NUMBER).format(num);
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? v
    : d.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function normalizeDateInput(value) {
  let normalizedValue = value;
  if (normalizedValue) {
    const [y, m, d] = normalizedValue.split("-");
    if (y && y.length > 4) {
      normalizedValue = `${y.slice(0, 4)}-${m || "01"}-${d || "01"}`;
    }
  }
  return normalizedValue;
}

function generateFilename(selectedPeriodOption, start, end) {
  const stamp = new Date().toISOString().split("T")[0];

  if (selectedPeriodOption?.type === "custom") {
    return `invoice_report_${start || "start"}_to_${end || "end"}_${stamp}.xlsx`;
  }

  if (selectedPeriodOption?.type === "fy") {
    return `invoice_report_${selectedPeriodOption.fy_label || "financial_year"}_${stamp}.xlsx`;
  }

  return `invoice_report_${selectedPeriodOption?.value || "report"}_${stamp}.xlsx`;
}

function buildBillingReportParams({
  selectedSubsidiary,
  selectedPeriodOption,
  customStartDate,
  customEndDate,
}) {
  const params = new URLSearchParams();
  const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;

  params.set("subsidiary_selected", selectedSubsidiary || "all");

  if (selectedOption.type === "fy") {
    if (selectedOption.fy_start) params.set("fy_start", selectedOption.fy_start);
    if (selectedOption.fy_end) params.set("fy_end", selectedOption.fy_end);
    return params;
  }

  if (selectedOption.type === "custom") {
    params.set("filter_type", "custom");
    if (customStartDate) params.set("start_date", customStartDate);
    if (customEndDate) params.set("end_date", customEndDate);
    return params;
  }

  params.set("filter_type", selectedOption.value);
  return params;
}

const QUICK_FILTER_OPTIONS = [
  { value: "last_7_days", label: "Last 7 days", type: "quick" },
  { value: "last_30_days", label: "Last 30 days", type: "quick" },
  { value: "last_90_days", label: "Last 90 days", type: "quick" },
  { value: "last_6_months", label: "Last 6 months", type: "quick" },
  { value: "custom", label: "Custom range", type: "custom" },
];

const FALLBACK_PERIOD_OPTION = QUICK_FILTER_OPTIONS[0];

const InvoiceReport = ({ isSidebarExpanded }) => {
  const [subsidiaries, setSubsidiaries] = useState([
    { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
  ]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState("all");

  const [, setFinancialYears] = useState([]);
  const [unifiedFilterOptions, setUnifiedFilterOptions] = useState(
    QUICK_FILTER_OPTIONS
  );
  const [selectedPeriodOption, setSelectedPeriodOption] = useState(
    FALLBACK_PERIOD_OPTION
  );
  const [hasLoadedPeriodOptions, setHasLoadedPeriodOptions] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [invoiceData, setInvoiceData] = useState([]);
  const [totalAmountSum, setTotalAmountSum] = useState("0.00");
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [searchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const res = await axiosInstance.get("/api/get_all_subsidiary", {
          params: { page_status: "Active" },
        });

        const raw = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];

        const list = raw
          .map((x) => ({
            subsidiary_id: x.subsidiary_id,
            subsidiary_name: x.Subsidiary_name,
          }))
          .filter((x) => x.subsidiary_id && x.subsidiary_name);

        const withAll = [
          { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
          ...list,
        ];

        setSubsidiaries(withAll);
        if (!withAll.some((s) => s.subsidiary_id === selectedSubsidiary)) {
          setSelectedSubsidiary("all");
        }
      } catch (e) {
        console.error("Error fetching subsidiaries:", e);
        setSubsidiaries([
          { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
        ]);
        setSelectedSubsidiary("all");
      }
    };

    fetchSubs();
  }, [selectedSubsidiary]);

  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const response = await axiosInstance.get("/api/financial-years");
        const list = Array.isArray(response?.data?.data) ? response.data.data : [];
        const fyOptions = list.map((financialYear) => ({
          label: `FY ${financialYear.fy_label}`,
          value: `fy_${financialYear.id}`,
          type: "fy",
          id: financialYear.id,
          fy_label: financialYear.fy_label,
          fy_start: financialYear.start_date,
          fy_end: financialYear.end_date,
          is_active: financialYear.is_active,
        }));

        const mergedOptions = [...fyOptions, ...QUICK_FILTER_OPTIONS];
        const activeFinancialYearOption =
          fyOptions.find((option) => option.is_active) ||
          fyOptions[0] ||
          FALLBACK_PERIOD_OPTION;

        setFinancialYears(list);
        setUnifiedFilterOptions(mergedOptions);
        setSelectedPeriodOption(activeFinancialYearOption);
      } catch (e) {
        console.error("Error fetching financial years:", e);
        setFinancialYears([]);
        setUnifiedFilterOptions(QUICK_FILTER_OPTIONS);
        setSelectedPeriodOption(FALLBACK_PERIOD_OPTION);
      } finally {
        setHasLoadedPeriodOptions(true);
      }
    };

    fetchFinancialYears();
  }, []);

  const filterQueryParams = useMemo(() => {
    return buildBillingReportParams({
      selectedSubsidiary,
      selectedPeriodOption,
      customStartDate,
      customEndDate,
    });
  }, [
    selectedSubsidiary,
    selectedPeriodOption,
    customStartDate,
    customEndDate,
  ]);

  const listQueryParams = useMemo(() => {
    const params = new URLSearchParams(filterQueryParams);
    params.set("page", String(currentPage));
    params.set("per_page", String(perPage));
    return params.toString();
  }, [filterQueryParams, currentPage, perPage]);

  const fetchInvoiceData = async () => {
    const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;

    if (
      selectedOption.type === "custom" &&
      (!customStartDate || !customEndDate)
    ) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axiosInstance.get(`/api/billing?${listQueryParams}`);
      const data = res.data;
      const pagination = data.pagination || {};

      const normalized = (data.billing_data || []).map((r) => ({
        srNo: r.sr_no || "",
        clientCompany: r.client_company || "",
        clientName: r.client_name || "",
        clientPan: r.client_pan || "",
        clientEmail: r.client_email || "",
        clientContactNumber: r.client_contact_number || "",
        invoiceId: r.invoice_id || "",
        invoiceNumber: r["invoice number"] || r.invoice_number || "",
        invoiceDate: r.invoice_date || "",
        dueDate: r.due_date || "",
        status: r.status || "",
        totalAmount: r.total_amount || "0.00",
      }));

      setInvoiceData(normalized);
      setInvoiceCount(data.number_of_invoices ?? normalized.length);
      setTotalAmountSum(data.total_amount_sum ?? "0.00");
      setCurrentPage(pagination.page || currentPage);
      setPerPage(pagination.per_page || perPage);
      setTotalPages(pagination.total_pages || 0);
      setTotalRecords(pagination.total_records || normalized.length);
    } catch (e) {
      setError(`Failed to load invoice data: ${e.message}`);
      setInvoiceData([]);
      setInvoiceCount(0);
      setTotalAmountSum("0.00");
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedSubsidiary,
    selectedPeriodOption?.value,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    if (!hasLoadedPeriodOptions) return;
    if (!selectedPeriodOption) return;

    if (selectedPeriodOption.type === "custom") {
      if (!customStartDate || !customEndDate) return;
    }

    fetchInvoiceData();
  }, [
    selectedSubsidiary,
    selectedPeriodOption,
    customStartDate,
    customEndDate,
    hasLoadedPeriodOptions,
    currentPage,
    perPage,
  ]);

  const filteredInvoices = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return invoiceData;

    return invoiceData.filter((inv) =>
      [
        inv.clientCompany,
        inv.clientName,
        inv.clientEmail,
        inv.invoiceNumber,
        inv.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [invoiceData, searchQuery]);

  const handlePeriodChange = (option) => {
    const nextSelectedOption = option || FALLBACK_PERIOD_OPTION;

    setSelectedPeriodOption(nextSelectedOption);
    setError("");

    if (nextSelectedOption.type !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const handleExport = async () => {
    const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;

    if (
      selectedOption.type === "custom" &&
      (!customStartDate || !customEndDate)
    ) {
      setError("Please select both start date and end date.");
      return;
    }

    try {
      const res = await axiosInstance.get(
        `/api/billing/export_excel?${filterQueryParams.toString()}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], {
        type:
          res.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = generateFilename(
        selectedOption,
        customStartDate,
        customEndDate
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (e) {
      console.error("Export error:", e);
      setError(`Export failed: ${e.message}`);
    }
  };

  const handlePaginationChange = (page, nextPerPage) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }

    if (nextPerPage !== perPage) {
      setPerPage(nextPerPage);
      setCurrentPage(1);
    }
  };

  return (
    <div
      className={`invoice-report-container ${
        isSidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"
      }`}
    >
      <div className="invoice-report-content">
        <div className="table-header">
  <div className="table-header-content">

    {/* LEFT SIDE */}
    <div className="table-left-side">
      <h2 className="jost-bold">Invoice Report</h2>
    </div>

    {/* RIGHT SIDE */}
    <div className="table-right-side">

      {/* Subsidiary Filter */}
      <SearchableDropdown
        className="report-filter-dropdown"
        triggerClassName="filter-select"
        value={selectedSubsidiary}
        options={subsidiaries}
        getOptionValue={(option) => option.subsidiary_id}
        getOptionLabel={(option) => option.subsidiary_name}
        onChange={(option) =>
          setSelectedSubsidiary(option?.subsidiary_id || "all")
        }
        placeholder="Select Entity"
        searchPlaceholder="Search subsidiary"
      />

      {/* Period Filter */}
      <SearchableDropdown
        className="report-filter-dropdown period-select"
        triggerClassName="filter-select period-select"
        value={selectedPeriodOption?.value || ""}
        options={unifiedFilterOptions}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        onChange={handlePeriodChange}
        placeholder="Select Period"
        searchPlaceholder="Search period"
      />

      {/* Custom Date Range */}
      {selectedPeriodOption?.type === "custom" && (
        <div className="custom-date-range">
          <input
            type="date"
            value={customStartDate}
            onChange={(e) => {
              setCustomStartDate(normalizeDateInput(e.target.value));
              setError("");
            }}
            className="date-input"
          />

          <span className="date-separator">to</span>

          <input
            type="date"
            value={customEndDate}
            onChange={(e) => {
              setCustomEndDate(normalizeDateInput(e.target.value));
              setError("");
            }}
            className="date-input"
          />
        </div>
      )}

      {/* Export Button */}
      <button className="export-button" onClick={handleExport}>
        Export Excel
      </button>

    </div>
  </div>
</div>

        {error && <div className="loading-message">{error}</div>}

        <div className="dashboard invoice-summary">
          <div className="cards-client invoice-summary-cards">
            <div className="card-client invoice-summary-card invoice-summary-count">
              <span className="ledger-summary-icon" aria-hidden="true">
                <FiFileText />
              </span>
              <div className="ledger-summary-copy">
                <span className="summary-label">Total Invoices</span>
                <span className="summary-value">{invoiceCount}</span>
              </div>
            </div>
            <div className="card-client invoice-summary-card invoice-summary-amount">
              <span className="ledger-summary-icon" aria-hidden="true">
                {/* <TbCurrencyRupee /> */}
                <TbCurrencyRupee />
              </span>
              <div className="ledger-summary-copy">
                <span className="summary-label">Total Amount</span>
                <span className="summary-value">{formatINR(totalAmountSum)}</span>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-message">Loading invoice data...</div>
        )}

        {!loading && (
          <div className="invoice-report-table-panel">
            <div className="data-list-scroll-body invoice-report-scroll-body">
              {/* <div className="data-list-table"> */}
                <table className="table multilevel-data">
                  <thead className="sticky-header">
                    <tr className="table-row">
                      <th className="center-align">Sr. No</th>
                      <th className="center-align">Invoice Date</th>
                      <th className="center-align">Due Date</th>
                      <th className="center-align">Invoice Number</th>
                      <th className="center-align">Client Pan</th>
                      <th>
                        <span
                          style={{
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                        >
                          Client Name
                        </span>
                      </th>
                      <th>
                        <span
                          style={{
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                        >
                          Client Company
                        </span>
                      </th>
                      <th className="center-align amount-column">Amount</th>
                      <th className="center-align">Status</th>
                      <th>
                        <span
                          style={{
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                        >
                          Client Email
                        </span>
                      </th>
                      <th className="center-align">Client Contact</th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: "#fff" }}>
                    {filteredInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="no-data">
                          No invoices found
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map((inv) => (
                        <tr
                          key={
                            inv.invoiceId ||
                            `${inv.invoiceNumber}-${inv.dueDate}-${Math.random()}`
                          }
                          className="table-row hoverable-row sub-td"
                        >
                          <td className="center-align">{inv.srNo || "-"}</td>
                          <td className="center-align">{formatDate(inv.invoiceDate)}</td>
                          <td className="center-align">{formatDate(inv.dueDate)}</td>
                          <td className="center-align">{inv.invoiceNumber || "-"}</td>
                          <td className="center-align">{inv.clientPan || "-"}</td>
                          <td>
                            {inv.clientName
                              ? inv.clientName
                                  .toLowerCase()
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() + word.slice(1)
                                  )
                                  .join(" ")
                              : "-"}
                          </td>
                          <td>
                            {inv.clientCompany
                              ? inv.clientCompany
                                  .toLowerCase()
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() + word.slice(1)
                                  )
                                  .join(" ")
                              : "-"}
                          </td>
                          <td className="center-align amount-column price-left-align">
                            {formatINR(inv.totalAmount)}
                          </td>
                          <td className="center-align status-cell">
                            <span
                              className={`invoice-report-status ${
                                (inv.status || "").toLowerCase() === "paid"
                                  ? "status-paid"
                                  : (inv.status || "").toLowerCase() === "unpaid"
                                  ? "status-unpaid"
                                  : (inv.status || "").toLowerCase().includes("partial")
                                  ? "status-partial"
                                  : "status-default"
                              }`}
                            >
                              {inv.status || "-"}
                            </span>
                          </td>
                          <td className="">{inv.clientEmail || "-"}</td>
                          <td className="center-align">
                            {inv.clientContactNumber || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              {/* </div> */}
            </div>
            <Pagination
              className="invoice-report-pagination"
              showPerPage={perPage}
              total={totalRecords}
              currentPage={currentPage}
              itemsPerPage={perPage}
              totalPages={totalPages}
              onPaginationChange={handlePaginationChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceReport;
